import {useCallback, useContext, useEffect, useState} from 'react';
import {debounce} from 'lodash-es';
import {Editor, Extension, JSONContent, useEditor} from '@tiptap/react';
import {StarterKit} from '@tiptap/starter-kit';
import {Placeholder as ExtensionPlaceholder} from '@tiptap/extension-placeholder';
import {ExtensionMention, MentionItem, MentionCategory} from '@common/messages/extensions/mention/Mention';
import {ContentEditable, ContentWrapper, Attachments, IconButtonWrapper, Toolbar, Wrapper}
  from '@common/messages/MessageEditor.styles';
import UploadModal from '@common/UploadModal';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import Chip from 'patient-ping-remedy/packages/chip';

export const extensions = [
  StarterKit,
  ExtensionPlaceholder.configure({
    placeholder: 'Add a message',
  }),
  ExtensionMention,
];

type Props = {
  isDisabled?: boolean;
  dischargeAttachmentsOnly?: boolean;
  onUpdate?(editor: Editor): void;
  onSubmit(content: JSONContent, attachments: File[]): void;
}

export default function MessageEditor(props: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const getMentionItems = useCallback(debounce(async (query: string, callback: (items: MentionItem[]) => void) => {
    const [users, groups] = await Promise.all([
      carecoApi?.getUsers({ name: query, page: 1, pageSize: 5 }),
      carecoApi?.getRosters({ name: query, page: 1, pageSize: 5 }),
    ]);

    callback([
      ...(users?.data.users ?? []).map((user) => ({
        id: user.id,
        label: `${user.first_name} ${user.last_name}`,
        user,
        category: MentionCategory.USER as const,
      })),
      ...(groups?.data.data ?? []).map((group) => ({
        id: group.id,
        label: group.name,
        category: MentionCategory.GROUP as const,
      })),
    ]);
  }, 500), []);

  const editor = useEditor({
    onUpdate({ editor }) {
      props.onUpdate?.(editor);
    },
    extensions: [
      ...extensions.filter((extension) => extension.name !== ExtensionMention.name),
      ExtensionMention.configure({
        suggestion: {
          items: ({ query }, ) =>
            new Promise((resolve) =>
              getMentionItems(query, (users) => resolve(users))
            ),
        },
      }),
      Extension.create({
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              setIsSubmitting(true);
              return true;
            },
            'Shift-Enter': () =>
              this.editor.commands.first(({ commands }) => [
                () => commands.newlineInCode(),
                () => commands.splitListItem('listItem'),
                () => commands.createParagraphNear(),
                () => commands.liftEmptyBlock(),
                () => commands.splitBlock(),
              ]),
          };
        },
      }),
    ],
  });

  useEffect(() => {
    editor?.setOptions({ editable: !props.isDisabled });
    editor?.commands.focus();
  }, [props.isDisabled]);

  useEffect(() => {
    if (isSubmitting) {
      handleSubmit();
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  useEffect(() => {
    document.body.addEventListener('click', handleClick);
    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  const handleClick = (event: MouseEvent) => {
    if (document.querySelector('#mention')?.contains(event.target as Node)) {
      editor?.commands.focus();
    }
  };

  const handleSubmit = () => {
    if (editor && editor.isEditable && !editor.isEmpty) {
      props.onSubmit(editor.getJSON(), attachments);
      editor.commands.clearContent();
      setIsToolbarOpen(false);
      setAttachments([]);
    }
  };

  const handleToolbar = () => {
    editor?.commands.focus();
    setIsToolbarOpen((isToolbarOpen) => !isToolbarOpen);
  };

  const handleMention = () => {
    const insertSpace = editor?.state.doc
      .textBetween(editor?.view.state.selection.from - 1, editor?.view.state.selection.to)
      .trim() !== '';

    editor?.commands.focus();
    editor?.commands.insertContent(`${insertSpace ? ' ' : ''}${ExtensionMention.options.suggestion.char}`);
  };

  const handleAttachment = (formData: FormData) => {
    setAttachments((files) => [...files, formData.get('file') as File]);
    setIsUploadOpen(false);
  };

  const removeAttachment = (index: number) => {
    setAttachments((files) => files.filter((_, i) => i !== index));
  };

  return (
    <>
      <Wrapper>
        <IconButtonWrapper>
          <IconButton iconClass={ICONS.plus} onClick={handleToolbar} />
        </IconButtonWrapper>
        <ContentWrapper>
          <ContentEditable editor={editor} />
          {attachments.length > 0 && (
            <Attachments>
              {attachments.map((file, index) => (
                <Chip key={index} onClick={() => removeAttachment(index)}>
                  {file.name}
                </Chip>
              ))}
            </Attachments>
          )}
        </ContentWrapper>
        <IconButtonWrapper>
          <IconButton
            iconClass={ICONS['paper-plane']}
            disabled={props.isDisabled || editor?.isEmpty}
            onClick={handleSubmit}
          />
        </IconButtonWrapper>
      </Wrapper>
      {isToolbarOpen && (
        <Toolbar>
          <IconButton
            iconClass={ICONS.at}
            disabled={!!document.querySelector('.suggestion')}
            onClick={handleMention}
          />
          <IconButton
            iconClass={ICONS.paperclip}
            onClick={() => setIsUploadOpen(true)}
          />
        </Toolbar>
      )}
      <UploadModal
        isOpen={isUploadOpen}
        isPending={false}
        dischargeOnly={props.dischargeAttachmentsOnly}
        close={() => setIsUploadOpen(false)}
        confirm={handleAttachment}
      />
    </>
  );
}
